import {
  useId,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Dialog as DSDialog, Heading } from '@digdir/designsystemet-react'
import { Button } from './Button'
import { handleDialogKeyDown, restoreDialogFocus } from './dialogFocus'
import { useT } from '@/shared/i18n'

// Native modal focus, inertness and top-layer placement stay on the same dialog.
// Body portalling preserves viewport geometry outside the application's zoom.

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'workspace'

export interface ModalProps {
  /** Whether the dialog is shown. When false, nothing is rendered. */
  open: boolean
  /** Requests closing; the owner may keep open true while busy. */
  onClose: () => void
  /** Heading text rendered in .modal__title (pre-translated by caller). */
  title?: ReactNode
  /** Dialog body content. */
  children: ReactNode
  /** Footer row content (typically Buttons); omitted hides .modal__ft. */
  footer?: ReactNode
  /** Panel max-width preset. Defaults to 'md'. */
  size?: ModalSize
}

const SIZE_MAX_WIDTH: Record<Exclude<ModalSize, 'workspace'>, number> = {
  sm: 440,
  md: 540,
  lg: 720,
  xl: 1160,
}

function isBackdrop(event: PointerEvent<HTMLDialogElement>) {
  if (event.target !== event.currentTarget) return false
  const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect()
  return event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom
}

/**
 * Presentational modal dialog.
 *
 * @example
 * <Modal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title={t('case.close.title')}
 *   footer={
 *     <>
 *       <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
 *       <Button variant="primary" onClick={submit}>{t('common.confirm')}</Button>
 *     </>
 *   }
 * >
 *   {t('case.close.body')}
 * </Modal>
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const t = useT()
  const panelRef = useRef<HTMLDialogElement>(null)
  const backdropPointer = useRef<number | null>(null)
  const titleId = useId()
  useLayoutEffect(() => restoreDialogFocus(panelRef.current))

  // Capture before DS's passive effect calls showModal; close before unmount.
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!open || !panel) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    return () => {
      const active = document.activeElement
      const remaining = [...document.querySelectorAll<HTMLDialogElement>('dialog[open]')].filter(dialog => dialog !== panel)
      const retainedFocus = active instanceof HTMLElement && remaining.some(dialog => dialog.contains(active)) ? active : null
      if (panel.open) panel.close()
      backdropPointer.current = null
      // Never steal focus from a dialog still open, or return to a closed parent.
      if (retainedFocus?.isConnected) retainedFocus.focus()
      else if (opener?.isConnected && !opener.closest('dialog:not([open])') &&
        (remaining.length === 0 || remaining.some(dialog => dialog.contains(opener)))) {
        opener.focus()
      }
    }
  }, [open])

  if (!open || typeof document === 'undefined') return null
  const hasTitle = title != null && typeof title !== 'boolean' && (typeof title !== 'string' || title.trim().length > 0)
  const panelStyle: CSSProperties | undefined = size === 'workspace' ? undefined : { maxWidth: SIZE_MAX_WIDTH[size] }

  return createPortal(
    <DSDialog
      ref={panelRef}
      open={open}
      modal
      placement="center"
      closeButton={false}
      closedby="closerequest"
      className={`modal${size === 'workspace' ? ' modal--workspace' : ''}`}
      aria-modal="true"
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={hasTitle ? undefined : t('chrome.dialog.label')}
      style={panelStyle}
      onKeyDown={event => handleDialogKeyDown(event, onClose)}
      onCancel={(event) => {
        if (event.target !== event.currentTarget) return
        // A close request must not bypass the owner's busy/dirty guards.
        event.preventDefault()
        event.stopPropagation()
        onClose()
      }}
      onPointerDown={(event) => {
        backdropPointer.current = event.button === 0 && event.isPrimary !== false && isBackdrop(event) ? event.pointerId : null
      }}
      onPointerUp={(event) => {
        const startedOutside = backdropPointer.current !== null && backdropPointer.current === event.pointerId
        backdropPointer.current = null
        if (startedOutside && isBackdrop(event)) {
          event.stopPropagation()
          onClose()
        }
      }}
      onPointerCancel={() => { backdropPointer.current = null }}
    >
      <DSDialog.Block className="modal__hd">
        {hasTitle ? (
          <Heading level={2} id={titleId} className="modal__title">
            {title}
          </Heading>
        ) : (
          <span />
        )}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon="x"
          aria-label={t('chrome.actions.close')}
          onClick={(event) => {
            event.stopPropagation()
            onClose()
          }}
        />
      </DSDialog.Block>

      <DSDialog.Block className="modal__body">{children}</DSDialog.Block>

      {footer != null && <DSDialog.Block className="modal__ft">{footer}</DSDialog.Block>}
    </DSDialog>,
    document.body,
  )
}
